import {
  Alert,
  AlertModal,
  Box,
  Button,
  ButtonLink,
  Heading,
  Pill,
  Spinner,
  Stack,
  Text,
  Toggle,
  useConfirmation,
  useNotifications,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React, { useCallback, useRef, useState } from 'react';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { ArcToggleStatus, CompanyLemLicensesQuery, LemLicenseStatus } from '../../lib/graphql';

type CompanyLemLicenses = CompanyLemLicensesQuery['companyLemLicenses'];
type LemLicense = CompanyLemLicenses['licenses'][number];

export interface CompanyLemViewProps {
  data: CompanyLemLicenses;
  onGrant: () => Promise<void>;
  onRevoke: () => Promise<void>;
}

function statusPillKind(status: LemLicenseStatus): React.ComponentProps<typeof Pill>['kind'] {
  switch (status) {
    case LemLicenseStatus.ACTIVE:
      return 'success';
    case LemLicenseStatus.PENDING:
      return 'info';
    default:
      return 'neutral';
  }
}

function toggleStateLabel(toggleStatus: ArcToggleStatus, active: boolean): string {
  switch (toggleStatus) {
    case ArcToggleStatus.AVAILABLE:
      return active ? 'Active' : 'Inactive';
    case ArcToggleStatus.NOT_LINKED:
      return 'Not linked';
    case ArcToggleStatus.NOT_CONFIGURED:
      return 'Not configured';
    default:
      return 'Unavailable';
  }
}

function toggleDisabledMessage(status: ArcToggleStatus): string | undefined {
  switch (status) {
    case ArcToggleStatus.AVAILABLE:
      return undefined;
    case ArcToggleStatus.NOT_LINKED:
      return 'Cannot create the license. The company does not have an ID on the Transporeon platform.';
    case ArcToggleStatus.NOT_CONFIGURED:
      return 'Visibility Standard plan isn’t provisioned in this environment yet, so the toggle is unavailable.';
    default:
      return 'Toggle unavailable.';
  }
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" gap="medium" alignItems="center">
    <Box style={{ minWidth: '120px' }}>
      <Text color="subtle">{label}</Text>
    </Box>
    {children}
  </Stack>
);

const LicenseDetails: React.FC<{ license: LemLicense }> = ({ license }) => (
  <Stack direction="column" gap="xsmall">
    <DetailRow label="License ID">
      <Text font="mono">{license.license_id}</Text>
    </DetailRow>
    <DetailRow label="Plan ID">
      <Text font="mono">{license.plan_id}</Text>
    </DetailRow>
    <DetailRow label="Plan name">
      <Text>{license.plan_name ?? '(no name)'}</Text>
    </DetailRow>
    <DetailRow label="Status">
      <Pill kind={statusPillKind(license.status)}>{license.status_raw}</Pill>
    </DetailRow>
    <DetailRow label="First activated at">
      <Text>
        <FormattedDate date={license.created_at} />
      </Text>
    </DetailRow>
  </Stack>
);

export const CompanyLemView: React.FC<CompanyLemViewProps> = ({ data, onGrant, onRevoke }) => {
  const isAdmin = useIsAdmin();
  const hasCompanyEditPermission = useHasScopes([BackofficeScope.companyWriteCompany]);
  const isPrivilegedUser = isAdmin || hasCompanyEditPermission;
  const notify = useNotifications();
  const toggleWrapperRef = useRef<HTMLDivElement | null>(null);
  const { pending, confirm, cancel, proceed, context } = useConfirmation<{
    intent: 'grant' | 'revoke';
  }>();
  const [mutating, setMutating] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const disabledMessage = toggleDisabledMessage(data.arc_toggle_status);
  const toggleAvailable = data.arc_toggle_status === ArcToggleStatus.AVAILABLE;
  const licenses = data.licenses;

  const handleToggle = useCallback(
    async ({ checked }: { checked: boolean }) => {
      if (mutating || !toggleAvailable || !isPrivilegedUser) {
        return;
      }
      const intent: 'grant' | 'revoke' = checked ? 'grant' : 'revoke';
      const confirmed = await confirm({ intent });
      if (!confirmed) {
        return;
      }
      setMutating(true);
      setLastError(null);
      try {
        if (intent === 'grant') {
          await onGrant();
          notify.success({ title: 'License granted', message: 'Granted the Visibility Standard license.' });
        } else {
          await onRevoke();
          notify.success({ title: 'License revoked', message: 'Revoked the Visibility Standard license.' });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        setLastError(message);
        notify.error({
          title: intent === 'grant' ? 'Could not grant license' : 'Could not revoke license',
          message,
        });
      } finally {
        setMutating(false);
      }
    },
    [mutating, toggleAvailable, isPrivilegedUser, confirm, onGrant, onRevoke, notify],
  );

  return (
    <Stack direction="column" gap="medium">
      <Stack direction="column" gap="small">
        <Stack direction="row" gap="medium" alignItems="center">
          <Heading size="large">LEM (License & Entitlement Management)</Heading>
          <ButtonLink
            kind="subtle"
            size="small"
            href="https://app.notion.com/p/trimble-tl/LEM-Primer-335c1b3dd25680a38330c502f8462d1f"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
          </ButtonLink>
        </Stack>
        <Text>
          Grant or revoke the <strong>Visibility Standard</strong> license for this company. Grants access to the skills
          requiring Visibility products in ARC, and the chatbot in our Visibility UI.
        </Text>
      </Stack>

      <Stack direction="column" gap="xsmall">
        {disabledMessage !== undefined ? <Alert kind="warning">{disabledMessage}</Alert> : null}
        {data.transporeon_account_id !== null ? (
          <Alert kind="info">
            {`Enabling it will grant the 'Visibility Standard' plan license to the Transporeon company with ID '${data.transporeon_account_id}'.`}
          </Alert>
        ) : null}
        <Box paddingBlockStart="medium" paddingBlockEnd="small">
          <div ref={toggleWrapperRef}>
            <Stack direction="row" gap="medium" alignItems="center">
              <Toggle
                label={toggleStateLabel(data.arc_toggle_status, data.arc_license_active)}
                checked={data.arc_license_active}
                disabled={!toggleAvailable || !isPrivilegedUser || mutating}
                onChange={handleToggle}
              />
              {mutating ? <Spinner size="small" /> : null}
            </Stack>
          </div>
        </Box>
        {!isPrivilegedUser ? (
          <Alert kind="info">You do not have permission to change this license. Contact a backoffice admin.</Alert>
        ) : null}
        {lastError !== null ? <Alert kind="danger">{lastError}</Alert> : null}
      </Stack>

      <Stack direction="column" gap="small">
        <Heading>License{licenses.length === 1 ? '' : 's'}</Heading>
        {licenses.length === 0 ? (
          <Text color="subtle">No license for this company.</Text>
        ) : (
          licenses.map((license) => <LicenseDetails key={license.license_id} license={license} />)
        )}
      </Stack>

      <AlertModal
        open={pending}
        onClose={cancel}
        title={
          context?.intent === 'grant' ? 'Grant the license to this company?' : 'Revoke the license from this company?'
        }
        description={
          context?.intent === 'grant'
            ? "This will grant the company the Visibility Standard license. The company's users will gain access to ARC skills that require the products granted by this plan, and to the chat window in our UI."
            : "This will revoke the company's Visibility Standard license. The company's users will lose access to ARC skills that require the products granted by this plan, and to the chat window in our UI."
        }
        trigger={toggleWrapperRef.current}>
        <Button kind="subtle" onClick={cancel}>
          Cancel
        </Button>
        <Button kind="primary" onClick={proceed}>
          Confirm
        </Button>
      </AlertModal>
    </Stack>
  );
};
